import * as signalR from "@microsoft/signalr";

import { IToken } from "../@types/Login";
import { IPatientDataChangedNotification } from "../@types/SignalR";
import { AUTH_TOKEN_STORAGE } from "../constants/localStorageKeys";
import { safeStorage } from "../utils/storage";

type PatientDataChangedHandler = (
  notification: IPatientDataChangedNotification
) => void;

class SignalRService {
  private connection: signalR.HubConnection | null = null;

  private patientDataChangedHandlers: PatientDataChangedHandler[] = [];

  private currentPatientId: number | null = null;

  private isConnecting = false;

  private getAccessToken(): string | null {
    const tokenResponse = safeStorage.getItem(AUTH_TOKEN_STORAGE);
    if (tokenResponse) {
      const parsedToken = JSON.parse(tokenResponse) as IToken;
      return parsedToken.idToken;
    }
    return null;
  }

  async connect(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    if (this.isConnecting) {
      return;
    }

    this.isConnecting = true;

    try {
      const hubUrl = `${
        process.env.REACT_APP_API_URL || ""
      }/Hubs/PatientNotifications`;

      // EN-5102: never build the token into the URL. With accessTokenFactory the client sends
      // "Authorization: Bearer" on negotiate and long polling; only the WebSocket/SSE connect
      // request (where browsers cannot set headers) carries ?access_token=. The factory runs on
      // every (re)connect, so a refreshed Cognito token is picked up.
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => this.getAccessToken() ?? "",
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      this.connection.on(
        "PatientDataChanged",
        (notification: IPatientDataChangedNotification) => {
          this.patientDataChangedHandlers.forEach((handler) =>
            handler(notification)
          );
        }
      );

      this.connection.onreconnected(async () => {
        console.log("SignalR reconnected");
        if (this.currentPatientId) {
          await this.joinPatientGroup(this.currentPatientId);
        }
      });

      this.connection.onclose((error) => {
        console.log("SignalR connection closed", error);
      });

      await this.connection.start();
      console.log("SignalR connected");
    } catch (error) {
      console.error("SignalR connection error:", error);
    } finally {
      this.isConnecting = false;
    }
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
        this.connection = null;
        this.currentPatientId = null;
      } catch (error) {
        console.error("SignalR disconnect error:", error);
      }
    }
  }

  async joinPatientGroup(patientId: number): Promise<void> {
    if (this.connection?.state !== signalR.HubConnectionState.Connected) {
      await this.connect();
    }

    try {
      if (this.currentPatientId && this.currentPatientId !== patientId) {
        await this.leavePatientGroup(this.currentPatientId);
      }

      await this.connection?.invoke("JoinPatientGroup", patientId);
      this.currentPatientId = patientId;
      console.log(`Joined patient group: ${patientId}`);
    } catch (error) {
      console.error("Failed to join patient group:", error);
    }
  }

  async leavePatientGroup(patientId: number): Promise<void> {
    if (this.connection?.state !== signalR.HubConnectionState.Connected) {
      return;
    }

    try {
      await this.connection?.invoke("LeavePatientGroup", patientId);
      if (this.currentPatientId === patientId) {
        this.currentPatientId = null;
      }
      console.log(`Left patient group: ${patientId}`);
    } catch (error) {
      console.error("Failed to leave patient group:", error);
    }
  }

  onPatientDataChanged(handler: PatientDataChangedHandler): () => void {
    this.patientDataChangedHandlers.push(handler);

    return () => {
      this.patientDataChangedHandlers = this.patientDataChangedHandlers.filter(
        (h) => h !== handler
      );
    };
  }

  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

export const signalRService = new SignalRService();
